import * as React from 'react';

import {
  DataGrid,
  DataGridSelection,
  FloatingToolbar,
  FontSemiBold,
  ListScreenLayout,
  Pagination,
} from '@rematter/ui-web';
import { useAtom, useAtomValue } from 'jotai';
import { LucideDot } from 'lucide-react';

import { BookingRecord } from '../../_graphql/queryBookingList';
import * as state from '../../_state/bookingList.state';
import { useInvoicesByBookings } from '../../hooks/useInvoicesByBookings';
import { customerPortalRoutes } from '../../routes';
import { bookingDocuments } from '../_shared/availableDocuments';
import {
  DownloadDocumentsButton,
  FetchDocumentsPopover,
  PrintDocumentsButton,
} from '../_shared/FetchDocuments';

import { BookingListFilterBar } from './BookingListFilterBar';
import { useBookingsColumns } from './hooks/useBookingsColumns';

import { useModuleLocation } from '~/shared/hooks/useModuleLocation';
import { useMultiRef } from '~/shared/hooks/useMultiRef';

//
// ⚛️ ⚛️ ⚛️ BOOKINGS LIST VIEW ⚛️ ⚛️ ⚛️
//
export const BookingsListView = () => {
  const [, navigate] = useModuleLocation();

  // Refs
  const menuRefs = useMultiRef<HTMLButtonElement>();

  // States

  const [selection, setSelection] = React.useState<DataGridSelection>(
    new Set(),
  );
  const [bookingIdForPrint, setBookingIdForPrint] = React.useState<string>();
  const [bookingIdForDownload, setBookingIdForDownload] =
    React.useState<string>();

  // Atoms
  const [limit = 50, setLimit] = useAtom(state.$limit);
  const [offset, setOffset] = useAtom(state.$offset);

  const { data, isLoading, error, loadingState, isFetching } = useAtomValue(
    state.$remoteBookings,
  );

  // Memoized values
  const bookings = React.useMemo(() => data?.bookings ?? [], [data?.bookings]);
  const total = React.useMemo(() => data?.total ?? 0, [data?.total]);

  const singleBookingId = React.useMemo(
    () =>
      bookingIdForPrint || bookingIdForDownload
        ? ([bookingIdForPrint, bookingIdForDownload].filter(
            Boolean,
          ) as string[])
        : [],
    [bookingIdForPrint, bookingIdForDownload],
  );

  const singleBookingInvoices = useInvoicesByBookings(singleBookingId);

  const singleBookingInvoiceNumbers = React.useMemo(
    () => singleBookingInvoices.map((it) => String(it.documentNumber)),
    [singleBookingInvoices],
  );

  const selectedBookings = React.useMemo(
    () => bookings.filter((it) => selection === 'all' || selection.has(it.id)),
    [bookings, selection],
  );

  const selectedBookingIds = React.useMemo(
    () => selectedBookings.map((it) => it.id),
    [selectedBookings],
  );

  const invoices = useInvoicesByBookings(selectedBookingIds);

  const invoiceNumbers = React.useMemo(
    () => invoices.map((it) => String(it.documentNumber)),
    [invoices],
  );

  // Callbacks
  const handlePaginationChange = React.useCallback(
    ({
      limit: newLimit,
      offset: newOffset,
    }: {
      limit: number;
      offset: number;
    }) => {
      setOffset(newOffset);
      setLimit(newLimit);
    },
    [setLimit, setOffset],
  );
  const handleCellClick = React.useCallback(
    ({ row: booking }: { row: BookingRecord }) => {
      navigate(customerPortalRoutes.bookingDetails, {
        params: { bookingId: booking.id },
      });
    },
    [navigate],
  );

  const columns = useBookingsColumns({
    onRowClick: handleCellClick,
    menuRefs,
    onPrint: setBookingIdForPrint,
    onDownload: setBookingIdForDownload,
  });

  return (
    <>
      <ListScreenLayout
        className="@BookingsListView"
        filters={<BookingListFilterBar />}
        footer={
          total > limit ? (
            <Pagination
              limit={limit}
              offset={offset}
              total={total}
              onChange={handlePaginationChange}
            />
          ) : undefined
        }
      >
        <DataGrid<BookingRecord>
          isLoading={
            (loadingState?.isInitialLoading ?? isLoading) || isFetching
          }
          columns={columns}
          dataSource={bookings}
          rowKey="id"
          selectionMode="multiple"
          onSelectionChange={setSelection}
          selectedKeys={selection}
          aria-label="Bookings list"
          fullWidth
          hasError={!!error}
          errorPanelProps={
            error ? { description: error.message, centered: true } : undefined
          }
          emptyPanelProps={{
            title: "It's empty here",
            description: 'No bookings found for the selected filters',
          }}
          className="w-full text-sm"
        />
      </ListScreenLayout>

      <FloatingToolbar visible={selectedBookings.length > 0}>
        <div className="flex items-center gap-2">
          <div className="flex min-w-[250px] items-center justify-end px-3 text-sm text-neutral/80">
            <div className="flex items-center gap-1.5">
              <FontSemiBold>{selectedBookings.length}</FontSemiBold>
              {selectedBookings.length === 1 ? 'booking' : 'bookings'} selected
            </div>
            <LucideDot size={16} className="text-neutral/40" />
            <span className="text-neutral/40">
              {invoices.length || 'no'}{' '}
              {invoices.length === 1 ? 'invoice' : 'invoices'}
            </span>
          </div>
          <PrintDocumentsButton
            originKey="booking-list"
            ids={invoiceNumbers}
            documents={bookingDocuments}
            isDisabled={!invoices.length}
          />
          <DownloadDocumentsButton
            originKey="booking-list"
            compressedFileName="bookings"
            ids={invoiceNumbers}
            documents={bookingDocuments}
            isDisabled={!invoices.length}
          />
        </div>
      </FloatingToolbar>

      {bookingIdForPrint && (
        <FetchDocumentsPopover
          isOpen
          type="print"
          documents={bookingDocuments}
          originKey="booking-list-row"
          triggerRef={menuRefs(bookingIdForPrint)}
          ids={singleBookingInvoiceNumbers}
          onOpenChange={() => setBookingIdForPrint(undefined)}
        />
      )}
      {bookingIdForDownload && (
        <FetchDocumentsPopover
          isOpen
          type="download"
          documents={bookingDocuments}
          originKey="booking-list-row"
          triggerRef={menuRefs(bookingIdForDownload)}
          ids={singleBookingInvoiceNumbers}
          onOpenChange={() => setBookingIdForDownload(undefined)}
        />
      )}
    </>
  );
};
